export const AI_ERROR_MESSAGES = {
  408: {
    en: "AI analysis is taking longer than expected. Please try again in a moment.",
    ar: "التحليل الذكي يستغرق وقتًا أطول من المتوقع. برجاء المحاولة مرة أخرى بعد قليل.",
  },
  401: {
    en: "Sorry, AI analysis isn't available right now. Please contact support and share code: 19190.",
    ar: "عذرًا، التحليل الذكي غير متاح حاليًا. برجاء التواصل مع الدعم مع الكود: 19190.",
  },
  403: {
    en: "Sorry, AI analysis isn't available right now. Please contact support and share code: 19191.",
    ar: "عذرًا، التحليل الذكي غير متاح حاليًا. برجاء التواصل مع الدعم مع الكود: 19191.",
  },
  429: {
    en: "The AI analysis quota has been reached. Please contact support and share code: 19193.",
    ar: "تم استهلاك الحصة المخصصة للتحليل الذكي. برجاء التواصل مع الدعم مع الكود: 19193.",
  },
  503: {
    en: "Sorry, AI analysis isn't available right now. Please contact support and share code: 19192.",
    ar: "عذرًا، التحليل الذكي غير متاح حاليًا. برجاء التواصل مع الدعم مع الكود: 19192.",
  },
  network: {
    en: "Could not reach the AI service. Please check your internet connection and try again.",
    ar: "تعذر الوصول إلى خدمة التحليل الذكي. برجاء التحقق من اتصال الإنترنت والمحاولة مرة أخرى.",
  },
};

const AI_ERROR_CODE_TO_STATUS = {
  assistant_processing_timeout: 408,
  rate_limit_exceeded: 429,
  insufficient_quota: 429,
  invalid_api_key: 401,
  organization_deactivated: 403,
  service_unavailable: 503,
};

const isArabic = (language) => language === "Arabic" || language === "ar";

export const getAiErrorMessage = (status, language) => {
  const error = AI_ERROR_MESSAGES[status];
  if (!error) return null;
  return isArabic(language) ? error.ar : error.en;
};

export const resolveAiErrorStatus = ({ code, message, status }) => {
  if (typeof status === "number" && AI_ERROR_MESSAGES[status]) {
    return status;
  }

  if (code && AI_ERROR_CODE_TO_STATUS[code]) {
    return AI_ERROR_CODE_TO_STATUS[code];
  }

  const normalized = String(message || "").toLowerCase();
  if (
    normalized.includes("rate_limit_exceeded") ||
    normalized.includes("insufficient_quota") ||
    normalized.includes("quota") ||
    normalized.includes("billing")
  ) {
    return 429;
  }

  return null;
};

export const buildAiErrorDetails = ({ code, language, message, status }) => {
  const resolvedStatus = resolveAiErrorStatus({ code, message, status });
  const resolvedMessage = getAiErrorMessage(resolvedStatus, language) || message || "AI request failed";

  return {
    code: code || null,
    status: resolvedStatus,
    message: resolvedMessage,
  };
};

export const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.code === "ABORT_ERR" ||
  error?.message === "Analysis request was cancelled";

const extractErrorDetails = async (response, language) => {
  try {
    const errorJson = await response.json();
    return buildAiErrorDetails({
      code: errorJson?.error?.code,
      language,
      message: errorJson?.error?.message || errorJson?.message,
      status: response.status,
    });
  } catch {
    return buildAiErrorDetails({ language, status: response.status });
  }
};

export const safeFetch = async (url, options, language) => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorDetails = await extractErrorDetails(response, language);
      const error = new Error(errorDetails.message);
      error.aiErrorCode = errorDetails.code;
      error.aiErrorStatus = errorDetails.status;
      throw error;
    }

    return response.json();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    if (
      error.message?.includes("Failed to fetch") ||
      error.message?.includes("NetworkError") ||
      error.message?.includes("Load failed")
    ) {
      const networkMessage = isArabic(language) ? AI_ERROR_MESSAGES.network.ar : AI_ERROR_MESSAGES.network.en;
      const networkError = new Error(networkMessage);
      networkError.aiErrorCode = "network_error";
      networkError.aiErrorStatus = null;
      throw networkError;
    }

    const sanitized = buildAiErrorDetails({
      code: error.aiErrorCode,
      language,
      message: error.message,
      status: error.aiErrorStatus,
    });
    const normalizedError = new Error(sanitized.message);
    normalizedError.aiErrorCode = sanitized.code;
    normalizedError.aiErrorStatus = sanitized.status;
    throw normalizedError;
  }
};
